/**
 * Client-side API: fetches to our Next.js API routes.
 * Use from client components only.
 */

import { sanitizeCustomerUpdateBody } from "./customer-update";
import {
  errorCodeFromBody,
  errorMessageFromBody,
  isApplicationErrorPayload,
} from "./server-response";
import { fetchWrapper } from "~/lib/fetch";

export interface DeploymentConfig {
  environment?: string;
  baseUrls?: {
    assets?: string;
    deployment?: string;
    bucket?: string;
    api?: string;
  };
  webhookBaseUrl?: string;
  [key: string]: unknown;
}

export async function getConfig(): Promise<DeploymentConfig> {
  const res = await fetchWrapper("/api/config");
  if (!res.ok) throw new Error("Failed to load config");
  return res.json() as Promise<DeploymentConfig>;
}

/** Parse client-visible message from failed fetch JSON (`{ code, message }` or `{ error }`). */
function extractErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  return errorMessageFromBody(data as Record<string, unknown>, fallback);
}

function responseUnauthorized(res: Response, data: Record<string, unknown>): boolean {
  if (res.status === 401 || res.status === 403) return true;
  const code = errorCodeFromBody(data);
  return code === 401 || code === 403;
}

export interface LoginParams {
  email: string;
  password: string;
  turnstileToken?: string;
}

export interface LoginResult {
  ok?: boolean;
  error?: string;
}

export async function login(body: LoginParams): Promise<LoginResult> {
  const res = await fetchWrapper("/api/auth/customer-auth", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as LoginResult & { error?: unknown };
  if (!res.ok) {
    return {
      error: extractErrorMessage(data, "Invalid email or password"),
    };
  }
  return data;
}

export interface RegisterParams {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  turnstileToken?: string;
}

export interface RegisterResult {
  ok?: boolean;
  error?: string;
  [key: string]: unknown;
}

export interface PasswordResetRequestParams {
  email: string;
}

export interface PasswordResetRequestResult {
  ok?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export async function requestPasswordReset(
  body: PasswordResetRequestParams,
): Promise<PasswordResetRequestResult> {
  const res = await fetchWrapper("/api/customer/password-reset-request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as PasswordResetRequestResult & { error?: unknown };
  if (!res.ok) {
    return {
      ...data,
      error: extractErrorMessage(data, "Password reset request failed. Please try again."),
    };
  }
  return data;
}

export interface PasswordResetConfirmParams {
  token: string;
  password: string;
  confirm_password: string;
}

export interface PasswordResetConfirmResult {
  ok?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

export async function confirmPasswordReset(
  body: PasswordResetConfirmParams,
): Promise<PasswordResetConfirmResult> {
  const res = await fetchWrapper("/api/customer/password-reset-confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as PasswordResetConfirmResult & { error?: unknown };
  if (!res.ok) {
    return {
      ...data,
      error: extractErrorMessage(data, "Password reset failed. Please try again."),
    };
  }
  if (data.ok === false) {
    return {
      ...data,
      error: extractErrorMessage(data, "Password reset failed. Please try again."),
    };
  }
  return data;
}

export async function register(body: RegisterParams): Promise<RegisterResult> {
  const res = await fetchWrapper("/api/customer-registration", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as RegisterResult & { error?: unknown };
  if (!res.ok) {
    return {
      error: extractErrorMessage(data, "Registration failed. Please try again."),
    };
  }
  return data;
}

export interface SubmitOtpParams {
  otp: string;
  remember_me: string;
}

export interface SubmitOtpResult {
  ok?: boolean;
  email?: string;
  error?: string;
}

export async function submitOtp(body: SubmitOtpParams): Promise<SubmitOtpResult> {
  const res = await fetchWrapper("/api/auth/customer-auth/otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as SubmitOtpResult & { error?: unknown };
  if (!res.ok) {
    return {
      error: extractErrorMessage(data, "Invalid code"),
    };
  }
  return data;
}

/** Map verified webhook user → NextAuth `session.update({ user })` patch. */
export function verifiedUserToSessionPatch(user: Record<string, unknown>) {
  let billing: Record<string, unknown> | undefined;
  const bd = user.billing_details;
  if (bd && typeof bd === "object" && !Array.isArray(bd)) {
    billing = bd as Record<string, unknown>;
  }

  return {
    first_name: typeof user.first_name === "string" ? user.first_name : undefined,
    last_name: typeof user.last_name === "string" ? user.last_name : undefined,
    email: typeof user.email === "string" ? user.email : undefined,
    phone: typeof user.phone === "string" ? user.phone : undefined,
    photo:
      (typeof user.photo === "string" && user.photo) ||
      (typeof user.image === "string" && user.image) ||
      undefined,
    status: typeof user.status === "string" ? user.status : undefined,
    created_at: typeof user.created_at === "string" ? user.created_at : undefined,
    last_updated: typeof user.last_updated === "string" ? user.last_updated : undefined,
    billing_details: billing,
  };
}

export interface VerifiedCustomerSessionResult {
  user?: Record<string, unknown>;
  raw?: unknown;
  error?: string;
  /** True when the auth service rejected the token (e.g. expired). */
  unauthorized?: boolean;
}

/**
 * GET /api/auth/customer-auth — verifies Bearer token (cookie) with backend and returns user.
 */
export async function fetchVerifiedCustomerSession(): Promise<VerifiedCustomerSessionResult> {
  const res = await fetchWrapper("/api/auth/customer-auth", {
    method: "GET",
    credentials: "include",
  });
  const data = (await res.json()) as Record<string, unknown> & {
    user?: unknown;
    raw?: unknown;
  };
  const rec = typeof data === "object" && data !== null ? data : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    return {
      error: extractErrorMessage(data, "Session verification failed"),
      unauthorized: responseUnauthorized(res, rec),
    };
  }
  const u = data.user;
  if (u && typeof u === "object" && !Array.isArray(u)) {
    return { user: u as Record<string, unknown>, raw: data.raw };
  }
  return { error: "Invalid session response" };
}

/** Cart order from GET /api/customer-orders?status=cart */
export interface CartOrderProduct {
  /** Backend cart-line id OR product id (depends on API shape) */
  id: string;
  /** When backend uses a separate product id field */
  product_id?: string;
  quantity: number;
  unit_base_price: number;
  name?: string;
  image?: string;
  business_id?: string;
  /** Product variant / attribute line (when present on API). */
  attribute?: {
    id: number;
    quantity?: number;
    price: number;
    name?: string;
    value?: string;
    image?: string;
  };
}

export interface CartOrder {
  id: string;
  business_id?: string;
  customer_id?: string;
  products?: CartOrderProduct[];
  total_price?: number;
  currency?: string;
  status?: string;
  created_at?: string;
  last_updated?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface GetCartOrderResult {
  orders: CartOrder[];
  error?: string;
  unauthorized?: boolean;
}

export async function getCartOrder(): Promise<GetCartOrderResult> {
  const res = await fetchWrapper("/api/customer-orders?status=cart&limit=50&offset=0", {
    credentials: "include",
  });
  const json = (await res.json()) as Record<string, unknown> & { data?: CartOrder[] };
  const rec = typeof json === "object" && json !== null ? json : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    const fallback = !res.ok
      ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
        "Failed to load cart"
      : "Failed to load cart";
    return {
      orders: [],
      error: extractErrorMessage(json, fallback),
      unauthorized: responseUnauthorized(res, rec),
    };
  }
  const orders = Array.isArray(json.data) ? json.data : [];
  return { orders };
}

export interface CreateCartOrderPayload {
  /** When provided, update an existing order instead of creating a new one. */
  id?: string;
  business_id: string;
  customer_id: string;
  products: Array<{
    id: string;
    quantity: number;
    unit_base_price: number;
    image?: string;
    name?: string;
    attribute?: {
      id: number;
      quantity: number;
      price: number;
      name?: string;
      value?: string;
      image?: string;
    };
  }>;
  total_price: number;
  currency: string;
  status: string;
  /** Optional: delivery details from checkout (structure defined by backend). */
  delivery_details?: unknown[];
  /** Optional: payment method used at checkout (e.g. "stripe", "paypal"). */
  payment_method?: string;
}

export interface CreateCartOrderResult {
  ok: boolean;
  order?: CartOrder;
  error?: string;
  unauthorized?: boolean;
}

export async function createCartOrder(
  payload: CreateCartOrderPayload,
): Promise<CreateCartOrderResult> {
  const res = await fetchWrapper("/api/customer-orders", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = (await res.json()) as { error?: unknown } & CartOrder;
  const rec =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    const fallback = !res.ok
      ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
        "Failed to create cart order"
      : "Failed to create cart order";
    return {
      ok: false,
      error: extractErrorMessage(data, fallback),
      unauthorized: responseUnauthorized(res, rec),
    };
  }
  return { ok: true, order: data as CartOrder };
}

export interface UpdateCartItemStatusParams {
  id: string;
  status: string;
}

export interface UpdateCartItemStatusResult {
  ok?: boolean;
  error?: string;
  unauthorized?: boolean;
}

export async function updateCartItemStatus(
  body: UpdateCartItemStatusParams,
): Promise<UpdateCartItemStatusResult> {
  const res = await fetchWrapper("/api/customer-orders", {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { error?: unknown };
  const rec =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    const fallback = !res.ok
      ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
        "Failed to update cart item"
      : "Failed to update cart item";
    return {
      ok: false,
      error: extractErrorMessage(data, fallback),
      unauthorized: responseUnauthorized(res, rec),
    };
  }
  return { ok: true };
}

/** PATCH payload to update an existing cart order (products, total, etc.). */
export interface UpdateCartOrderPayload {
  id: string;
  products: Array<{
    id: string;
    quantity: number;
    unit_base_price: number;
    image?: string;
    name?: string;
    attribute?: {
      id: number;
      quantity: number;
      price: number;
      name?: string;
      value?: string;
      image?: string;
    };
  }>;
  total_price: number;
  currency: string;
  status: string;
  delivery_details?: unknown[];
  payment_method?: string;
}

export interface UpdateCartOrderResult {
  ok?: boolean;
  error?: string;
  unauthorized?: boolean;
}

export async function updateCartOrder(
  payload: UpdateCartOrderPayload,
): Promise<UpdateCartOrderResult> {
  const res = await fetchWrapper("/api/customer-orders", {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = (await res.json()) as { error?: unknown };
  const rec =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    const fallback = !res.ok
      ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
        "Failed to update cart"
      : "Failed to update cart";
    return {
      ok: false,
      error: extractErrorMessage(data, fallback),
      unauthorized: responseUnauthorized(res, rec),
    };
  }
  return { ok: true };
}

/** Profile image upload (POST /api/upload-image). */
export interface UploadProfileImageParams {
  userId: string;
  imageType?: "users" | string;
  file: File;
}

export interface UploadProfileImageResult {
  ok?: boolean;
  image_url?: string;
  url?: string;
  error?: string;
  [key: string]: unknown;
}

export async function uploadProfileImage(
  body: UploadProfileImageParams,
): Promise<UploadProfileImageResult> {
  const form = new FormData();
  form.append("user_id", body.userId);
  form.append("image_type", body.imageType ?? "users");
  form.append("file", body.file);

  const res = await fetchWrapper("/api/upload-image", {
    method: "POST",
    body: form,
  });
  const data = (await res.json()) as UploadProfileImageResult & { error?: unknown };
  if (!res.ok) {
    return {
      ok: false,
      error: extractErrorMessage(data, "Failed to upload image"),
    };
  }
  return {
    ...data,
    ok: true,
  };
}

/** Resolve a stored image key (e.g. users/xxx.png) to a display URL (POST /api/get-image). */
export async function fetchProfileImageUrl(
  imageUrl: string,
): Promise<{ image_url?: string; error?: string }> {
  const res = await fetchWrapper("/api/get-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ image_url: imageUrl }),
  });
  const data = (await res.json()) as {
    image_url?: unknown;
    error?: unknown;
  };
  if (!res.ok) {
    return {
      error: extractErrorMessage(data, "Failed to load image"),
    };
  }
  const url =
    typeof data.image_url === "string" && data.image_url.trim()
      ? data.image_url.trim()
      : undefined;
  return { image_url: url };
}

export interface FetchStripePaymentLinkParams {
  currency: string;
  product_name: string;
  customer_id: string;
  order_id: string;
}

export interface FetchStripePaymentLinkResult {
  code?: string | number;
  link?: string;
  error?: string;
  [key: string]: unknown;
}

/**
 * GET /api/payments/stripe — returns `{ code, link }` from upstream when successful.
 * Amount is calculated server-side from the order service fee; do not pass amount.
 */
export async function fetchStripePaymentLink(
  params: FetchStripePaymentLinkParams,
): Promise<FetchStripePaymentLinkResult> {
  const sp = new URLSearchParams();
  sp.set("currency", params.currency);
  sp.set("product_name", params.product_name);
  sp.set("customer_id", params.customer_id);
  sp.set("order_id", params.order_id);
  const res = await fetchWrapper(`/api/payments/stripe?${sp.toString()}`, {
    credentials: "include",
  });
  const data = (await res.json()) as FetchStripePaymentLinkResult & { error?: unknown };
  const rec =
    typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  if (!res.ok || isApplicationErrorPayload(rec)) {
    return {
      ...data,
      error: extractErrorMessage(
        data as Record<string, unknown>,
        !res.ok
          ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
              "Could not start Stripe checkout"
          : "Could not start Stripe checkout",
      ),
    };
  }
  return data;
}

export interface FetchServiceFeeParams {
  order_id: string;
}

export interface FetchServiceFeeResult {
  ok?: boolean;
  order_id?: string;
  total_price?: number;
  flat_fee?: number;
  commission_rate?: number;
  commission_fee?: number;
  service_fee?: number;
  total_with_fee?: number;
  error?: string;
  code?: number;
}

/** POST /api/service-fee — fee breakdown for a cart order. */
export async function fetchServiceFee(
  params: FetchServiceFeeParams,
): Promise<FetchServiceFeeResult> {
  const res = await fetchWrapper("/api/service-fee", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ order_id: params.order_id }),
  });
  const data = (await res.json()) as FetchServiceFeeResult & {
    message?: string;
  };
  const rec =
    typeof data === "object" && data !== null
      ? (data as Record<string, unknown>)
      : {};
  if (!res.ok || data.ok === false || isApplicationErrorPayload(rec)) {
    return {
      ...data,
      ok: false,
      error: extractErrorMessage(
        data as Record<string, unknown>,
        !res.ok
          ? `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`.trim() ||
              "Could not calculate service fee"
          : "Could not calculate service fee",
      ),
    };
  }
  return { ...data, ok: true };
}

/** Profile update (PATCH /api/customer-update). Fields match DB: first_name, last_name, photo, email, phone, billing_details. */
export interface UpdateProfileParams {
  firstName?: string;
  lastName?: string;
  photo?: string | null;
  email?: string;
  phone?: string;
  /** Billing/details sent as JSON (e.g. address1, address2, country) */
  billingDetails?: { address1?: string; address2?: string; country?: string; [key: string]: unknown };
}

export interface UpdateProfileResult {
  ok?: boolean;
  error?: string;
  [key: string]: unknown;
}

export async function updateProfile(
  body: UpdateProfileParams,
): Promise<UpdateProfileResult> {
  const payload: Record<string, unknown> = sanitizeCustomerUpdateBody({
    first_name: body.firstName,
    last_name: body.lastName,
    email: body.email,
    phone: body.phone,
    photo: body.photo ?? undefined,
    ...(body.billingDetails && Object.keys(body.billingDetails).length > 0
      ? { billing_details: body.billingDetails as Record<string, unknown> }
      : {}),
  });
  const res = await fetchWrapper("/api/customer-update", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = (await res.json()) as UpdateProfileResult &
    Record<string, unknown> & { error?: unknown };
  if (!res.ok) {
    return {
      error: extractErrorMessage(data, "Failed to update profile"),
    };
  }
  if (isApplicationErrorPayload(data)) {
    return {
      error: extractErrorMessage(data, "Failed to update profile"),
    };
  }
  return data;
}
